import { isDefined, isNil } from "@clipboard-health/util-ts";
import { MIN_SHIFT_LENGTH_FOR_MANDATORY_BREAK_IN_MINUTES } from "@src/appV2/redesign/Shift/constants";
import { ExtraWorkedTimeType, type TimeRange } from "@src/appV2/Shifts/Shift/types";
import { differenceInMinutes, parseISO } from "date-fns";

export function doesShiftHaveWorkedBreak(params: {
  mandatoryBreakDurationInMinutes: number;
  clockInOut: TimeRange;
  lunchInOut: TimeRange | undefined;
}): boolean {
  const { mandatoryBreakDurationInMinutes, clockInOut, lunchInOut } = params;

  const workDurationInMinutes =
    isDefined(clockInOut.start) && isDefined(clockInOut.end)
      ? differenceInMinutes(parseISO(clockInOut.end), parseISO(clockInOut.start))
      : 0;
  const breakDurationInMinutes =
    isDefined(lunchInOut?.start) && isDefined(lunchInOut?.end)
      ? differenceInMinutes(parseISO(lunchInOut.end), parseISO(lunchInOut.start))
      : 0;

  return (
    workDurationInMinutes > MIN_SHIFT_LENGTH_FOR_MANDATORY_BREAK_IN_MINUTES &&
    breakDurationInMinutes < mandatoryBreakDurationInMinutes
  );
}

export interface AuthorizingEmployee {
  name: string;
  signature: string;
}

export function getExtraWorkedTime(props: {
  mandatoryBreakDurationInMinutes: number;
  clockInOut: TimeRange;
  lunchInOut: TimeRange | undefined;
  breakAuthorizationReason: string | undefined;
  authorizingEmployee?: AuthorizingEmployee;
}):
  | Array<{
      type: ExtraWorkedTimeType;
      authorization: { reason: string; authorizingEmployee?: AuthorizingEmployee };
    }>
  | undefined {
  const {
    mandatoryBreakDurationInMinutes,
    clockInOut,
    lunchInOut,
    breakAuthorizationReason,
    authorizingEmployee,
  } = props;

  const hasWorkedBreak =
    isDefined(breakAuthorizationReason) &&
    doesShiftHaveWorkedBreak({ mandatoryBreakDurationInMinutes, clockInOut, lunchInOut });

  if (!hasWorkedBreak) {
    return undefined;
  }

  const authorization = {
    reason: breakAuthorizationReason,
    ...(authorizingEmployee ? { authorizingEmployee } : {}),
  };
  return [{ type: ExtraWorkedTimeType.BREAK, authorization }];
}

export interface MissedPunch {
  type: ExtraWorkedTimeType.FULL_SHIFT | ExtraWorkedTimeType.BREAK;
  reason: string;
}

/**
 * Constructs extraWorkedTime entries for missed punch requests.
 *
 * Returns FULL_SHIFT type with reason for missed punch requests.
 */
export function getMissedPunchExtraWorkedTime(props: {
  missedPunches: MissedPunch[] | undefined;
}): Array<{
  type: ExtraWorkedTimeType;
  authorization: { reason: string };
}> {
  const { missedPunches } = props;

  const fullShiftPunch = (missedPunches ?? []).find(
    (p) => p.type === ExtraWorkedTimeType.FULL_SHIFT
  );

  if (isNil(fullShiftPunch)) {
    return [];
  }

  return [
    { type: ExtraWorkedTimeType.FULL_SHIFT, authorization: { reason: fullShiftPunch.reason } },
  ];
}
